import { useMemo } from "react";
import {
  loadDoctorConsoleConfig,
  getSectionByKey,
  buildRuleContext,
} from "../engine/loadConfig";
import { resolveSectionFieldLayout } from "../engine/resolveSectionFields";
import { useDoctorConsoleRemoteConfig } from "./useDoctorConsoleRemoteConfig";

/**
 * Resolves the per-doctor field layout for every History sub-section, using the
 * exact same merge + resolution pipeline as the Doctor Console (published
 * override → loadDoctorConsoleConfig → resolveSectionFieldLayout).
 *
 * Returned map is keyed by the sub-section `key` (e.g. "History of Past Illness",
 * "Allergies", "Menstrual History"), matching the medicalBackground data paths
 * used by the Case Sheet.
 *
 * Consumers pass each layout into the shared HistoriesData components as their
 * optional `fieldLayout` prop. When a doctorId is absent or the fetch has not
 * resolved yet, the map is empty and components fall back to their built-in
 * default layout — so no existing behavior changes.
 */
export const useHistoryFieldLayouts = (doctorId, patient = {}) => {
  const { config: remoteOverride } = useDoctorConsoleRemoteConfig(doctorId);

  return useMemo(() => {
    const config = loadDoctorConsoleConfig(remoteOverride);
    const ruleContext = buildRuleContext({ patient });
    const historySection = getSectionByKey(config, "History");

    const layouts = {};
    (historySection?.subSections || []).forEach((sub) => {
      if (!sub?.key) return;
      layouts[sub.key] = sub.fieldSet
        ? resolveSectionFieldLayout(sub, config, ruleContext)
        : null;
    });
    return layouts;
  }, [remoteOverride, patient]);
};

export default useHistoryFieldLayouts;
